import type { UserDto } from '@pietrochip/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as api from '../api/endpoints';
import { ApiError, NetworkError } from '../api/client';

/**
 * Chi è collegato.
 *
 * La sessione vive in un cookie `httpOnly`, quindi il client non può leggerla:
 * all'avvio si chiede al server chi siamo. Finché la risposta non arriva non si
 * decide niente — mostrare la schermata di accesso a chi è già dentro sarebbe
 * un lampo fastidioso a ogni ricarica.
 */

export type AuthStatus = 'checking' | 'anonymous' | 'authenticated';

type AuthContextValue = {
  status: AuthStatus;
  user: UserDto | null;
  /**
   * L'accesso è libero: l'utente qui sopra è quello di riserva configurato sul
   * server, non chi ha davanti il tablet. Serve a non mostrare un pulsante
   * «Esci» che non porta da nessuna parte.
   */
  freeAccess: boolean;
  /** La verifica iniziale è fallita per un problema di rete, non di credenziali. */
  offline: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: UserDto) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUserState] = useState<UserDto | null>(null);
  const [freeAccess, setFreeAccess] = useState(false);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const { user: current, freeAccess: free } = await api.fetchMe();
        if (cancelled) return;
        setUserState(current);
        setFreeAccess(free);
        setStatus('authenticated');
      } catch (error) {
        if (cancelled) return;
        // 401 significa "non collegato"; qualsiasi altro esito (server spento,
        // rete assente) non è una risposta sulle credenziali e va detto.
        setOffline(!(error instanceof ApiError) && error instanceof NetworkError);
        setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user: authenticated } = await api.login({ email, password });
    setUserState(authenticated);
    // Da qui in poi la sessione è vera, anche se l'accesso libero resta attivo
    // per chi arriva senza: il pulsante «Esci» torna a funzionare e va mostrato.
    setFreeAccess(false);
    setStatus('authenticated');
    setOffline(false);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Anche se la chiamata fallisce, localmente si è fuori: il cookie è
      // comunque in scadenza, e restare "dentro" a metà è peggio.
      setUserState(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, freeAccess, offline, signIn, signOut, setUser: setUserState }),
    [status, user, freeAccess, offline, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth va usato dentro AuthProvider');
  return context;
}
